/**
 * Standalone Adapter for TagadaPay SDK v2
 *
 * Provides a vanilla JS interface for using the SDK without a framework.
 * Suitable for standard HTML/JS sites or other frameworks (Svelte, Angular, etc).
 *
 * This layer is a very thin wrapper over the Core SDK.
 */

import { TagadaClient, TagadaClientConfig, TagadaState } from '../core/client';
import { ApiClient } from '../core/resources/apiClient';
import { CheckoutResource } from '../core/resources/checkout';
import { getAssignedStepConfig, getAssignedPaymentFlowId } from '../core/funnelClient';
import { detectEnvironment } from '../core/config/environment';
import { getBasisTheoryApiKey as _getBtApiKey, getBasisTheoryTenantId as _getBtTenantId } from '../../react/config/payment';
import { publishTrackingGlobal } from '../core/utils/clickIdResolver';
import {
    installScriptErrorRelay,
    isScriptsPreview,
    lineOfCode,
    previewWrapperParts,
    publishPreviewFlag,
    requestDraftScripts,
} from '../core/utils/scriptsPreview';

// ============================================================================
// AUTO SCRIPT INJECTOR
// ============================================================================
// Automatically injects and executes scripts from stepConfig when SDK loads
// This runs for all standalone SDK users (not just React apps)

interface StepConfigScript {
    name: string;
    enabled: boolean;
    content: string;
    position?: 'head-start' | 'head-end' | 'body-start' | 'body-end';
}

/**
 * Parse step config from window variable or meta tag
 */
function parseStepConfigScripts(): StepConfigScript[] {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return [];
    }

    // Try to get stepConfig (uses same logic as getAssignedStepConfig)
    const stepConfig = getAssignedStepConfig();
    if (stepConfig?.scripts) {
        return stepConfig.scripts.filter(s => s.enabled);
    }

    return [];
}

type ParsedScriptElement =
    | { type: 'external'; src: string; async?: boolean; defer?: boolean }
    | { type: 'inline'; code: string }
    | { type: 'noscript'; html: string }
    | { type: 'html'; html: string };

/**
 * Classify a chunk found OUTSIDE <script>/<noscript> tags. Such chunks are HTML
 * markup (e.g. <link rel="preconnect">, <meta>, <img>/<iframe> pixels) far more
 * often than bare JS. Injecting markup as inline JS throws an uncatchable
 * parse-time `SyntaxError: Unexpected token '<'` that aborts the whole <script>
 * and silently kills sibling tracking scripts bundled in the same snippet.
 */
function classifyLooseChunk(chunk: string): ParsedScriptElement | null {
    if (!chunk || /^<!--[\s\S]*?-->$/.test(chunk)) return null;
    const looksLikeMarkup = /^</.test(chunk) || /<\/?[a-zA-Z][\w-]*[\s/>]/.test(chunk);
    return looksLikeMarkup ? { type: 'html', html: chunk } : { type: 'inline', code: chunk };
}

/**
 * Parse script content that may contain multiple <script>/<noscript> tags plus
 * interleaved non-script markup. Returns ordered elements to inject individually
 * so one malformed chunk can never break its siblings.
 */
function parseScriptContent(content: string): ParsedScriptElement[] {
    const trimmed = content.trim();

    // No script/noscript tags → treat the whole thing as raw JS.
    if (!/<(?:script|noscript)[\s>]/i.test(trimmed)) {
        return trimmed ? [{ type: 'inline', code: trimmed }] : [];
    }

    const elements: ParsedScriptElement[] = [];
    const tagRegex = /<(script|noscript)([^>]*)>([\s\S]*?)<\/\1>/gi;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tagRegex.exec(trimmed)) !== null) {
        const between = classifyLooseChunk(trimmed.slice(lastIndex, match.index).trim());
        if (between) elements.push(between);

        const [, tagName, attrs, body] = match;
        if (tagName.toLowerCase() === 'noscript') {
            if (body.trim()) elements.push({ type: 'noscript', html: body.trim() });
        } else {
            const srcMatch = attrs.match(/src=["']([^"']+)["']/i);
            if (srcMatch) {
                elements.push({
                    type: 'external',
                    src: srcMatch[1],
                    async: /\basync\b/i.test(attrs),
                    defer: /\bdefer\b/i.test(attrs),
                });
            }
            if (body.trim()) elements.push({ type: 'inline', code: body.trim() });
        }
        lastIndex = match.index + match[0].length;
    }

    const trailing = classifyLooseChunk(trimmed.slice(lastIndex).trim());
    if (trailing) elements.push(trailing);

    return elements;
}

/** Inject a DOM element at the requested position. */
function injectAtPosition(element: Element, position: string): void {
    switch (position) {
        case 'head-start':
            if (document.head.firstChild) document.head.insertBefore(element, document.head.firstChild);
            else document.head.appendChild(element);
            break;
        case 'head-end':
            document.head.appendChild(element);
            break;
        case 'body-start':
            if (document.body.firstChild) document.body.insertBefore(element, document.body.firstChild);
            else document.body.appendChild(element);
            break;
        case 'body-end':
        default:
            document.body.appendChild(element);
            break;
    }
}

/**
 * Inject a (possibly multi-tag) script at the specified position.
 */
function injectScript(script: StepConfigScript, index: number): void {
    const position = script.position || 'body-end';
    const content = script.content.trim();
    if (!content) return;

    // Skip if any element from this script is already in the DOM.
    if (document.querySelector(`[data-tagada-stepconfig-index="${index}"]`)) {
        return;
    }

    parseScriptContent(content).forEach((element, elemIndex) => {
        const elemId = `tagada-stepconfig-script-${index}-${elemIndex}`;

        if (element.type === 'external') {
            const el = document.createElement('script');
            el.id = elemId;
            el.setAttribute('data-tagada-stepconfig-script', 'true');
            el.setAttribute('data-tagada-stepconfig-index', String(index));
            el.setAttribute('data-script-name', script.name);
            el.src = element.src;
            if (element.async) el.async = true;
            if (element.defer) el.defer = true;
            injectAtPosition(el, position);

        } else if (element.type === 'inline') {
            // Inline JS — wrap with error handling. String concat (not template
            // literals) so merchant code with backticks / ${...} stays intact.
            const el = document.createElement('script');
            el.id = elemId;
            el.setAttribute('data-tagada-stepconfig-script', 'true');
            el.setAttribute('data-tagada-stepconfig-index', String(index));
            el.setAttribute('data-script-name', script.name);
            // The merchant code starts on line 4 of the wrapper.
            const preview = previewWrapperParts(index, elemIndex, 4, lineOfCode(content, element.code));
            el.textContent = '(function() {\n' +
                '  try {\n' +
                '    // Script: ' + script.name + '\n' +
                element.code + '\n' +
                '  } catch (error) {\n' +
                '    console.error("[TagadaPay] StepConfig script error:", error);' + preview.catchHook + '\n' +
                '  }\n' +
                '})();' + preview.sourceUrl;
            injectAtPosition(el, position);

        } else if (element.type === 'noscript') {
            const el = document.createElement('noscript');
            el.id = elemId;
            el.setAttribute('data-tagada-stepconfig-script', 'true');
            el.setAttribute('data-tagada-stepconfig-index', String(index));
            el.innerHTML = element.html;
            injectAtPosition(el, position);

        } else if (element.type === 'html') {
            // Non-script markup (link/meta/img/iframe pixels) — inject as real DOM.
            const template = document.createElement('template');
            template.innerHTML = element.html;
            Array.from(template.content.childNodes).forEach(node => {
                if (node.nodeType !== 1 /* ELEMENT_NODE */) return;
                const el = node as HTMLElement;
                el.setAttribute('data-tagada-stepconfig-script', 'true');
                el.setAttribute('data-tagada-stepconfig-index', String(index));
                el.setAttribute('data-script-name', script.name);
                injectAtPosition(el, position);
            });
        }
    });
}

/**
 * Auto-inject all stepConfig scripts
 * Called automatically when SDK loads
 * Can also be called manually if needed (e.g., after dynamic config update)
 */
export function injectStepConfigScripts(): void {
    const scripts = parseStepConfigScripts();
    if (scripts.length === 0) return;

    scripts.forEach((script, index) => {
        injectScript(script, index);
    });
}

/**
 * Set to `true` by a loader that starts the step scripts itself, once its page
 * is ready for them: Studio's split islands build, which must set up
 * `window.Tagada` first and may still hand the page to another bundle. Absent,
 * the scripts start when this module loads, as they always have.
 */
export const HOLD_STEP_CONFIG_SCRIPTS = '__TGD_HOLD_STEP_CONFIG_SCRIPTS__';

let stepConfigScriptsStarted = false;

/**
 * Inject `stepConfig.scripts` after the current script execution completes
 * (requestIdleCallback, or a setTimeout fallback). Once per page load: this
 * module calls it itself unless `window[HOLD_STEP_CONFIG_SCRIPTS]` is set.
 */
export function startStepConfigScripts(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (stepConfigScriptsStarted) return;
    stepConfigScriptsStarted = true;

    const runInjection = () => {
        // Wait for body to be available
        if (document.body) {
            injectStepConfigScripts();
        } else {
            // Body not ready, wait for DOMContentLoaded
            document.addEventListener('DOMContentLoaded', injectStepConfigScripts, { once: true });
        }
    };

    const scheduleInjection = () => {
        // Run after current script execution completes
        if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(runInjection, { timeout: 100 });
        } else {
            setTimeout(runInjection, 0);
        }
    };

    if (isScriptsPreview()) {
        // In the CRM's scripts preview, wait for the draft list before injecting (#TAG-3352).
        requestDraftScripts().then(() => {
            installScriptErrorRelay();
            scheduleInjection();
        });
    } else {
        scheduleInjection();
    }
}

// Auto-run script injection when SDK loads
// Uses requestIdleCallback or setTimeout fallback for non-blocking execution
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Publish click_id (URL > cookie) to window.TagadaPay.tracking IMMEDIATELY
    // — synchronously, before any merchant stepConfig.scripts run. This is what
    // ad-tracker postback snippets read, so it must exist before they execute.
    // No DOM dependencies, safe to call right now.
    try {
        publishTrackingGlobal();
    } catch (error) {
        console.error('[TagadaPay] Failed to publish tracking global:', error);
    }

    // Merchant scripts read TagadaPay.isPreview, so it exists before any of them runs.
    publishPreviewFlag();

    if (!(window as any)[HOLD_STEP_CONFIG_SCRIPTS]) startStepConfigScripts();
}

/**
 * Factory function to create a Tagada Client instance.
 * Features (like funnel) can be toggled via the config.
 */
export function createTagadaClient(config: TagadaClientConfig = {}): TagadaClient {
    return new TagadaClient(config);
}

// Re-export Core Classes
export { TagadaClient, ApiClient, CheckoutResource };

// Re-export Resource Classes (for standalone resolvers / builders)
export { ShippingRatesResource } from '../core/resources/shippingRates';
export { SendCloudResource } from '../core/resources/sendcloud';
export type {
  SendCloudCredentials,
  FetchServicePointsParams,
  OpenServicePointPickerInput,
  OpenServicePointPickerCallbacks,
} from '../core/resources/sendcloud';
export { PaymentsResource } from '../core/resources/payments';
// On-chain crypto (`crypto_onchain`): paid inline, never through pay-v2.
export {
  CryptoOnchainResource,
  CRYPTO_ONCHAIN_PAYMENT_METHOD,
  CRYPTO_ONCHAIN_STATUS_POLL_MS,
  CRYPTO_ONCHAIN_FINAL_PHASES,
  isCryptoOnchainMethod,
  defaultCryptoOnchainSelection,
  selectCryptoOnchainNetwork,
  cryptoOnchainPhase,
  cryptoOnchainSecondsLeft,
  formatCryptoOnchainCountdown,
} from '../core/resources/cryptoOnchain';
export type {
  CryptoOnchainAsset,
  CryptoOnchainNetwork,
  CryptoOnchainPayment,
  CryptoOnchainStatus,
  CryptoOnchainPhase,
  CryptoOnchainOptionsResult,
  CryptoOnchainPaymentResult,
  CryptoOnchainStatusResult,
  CryptoOnchainOptionsFailureCode,
  CryptoOnchainPaymentFailureCode,
  CryptoOnchainStatusFailureCode,
} from '../core/resources/cryptoOnchain';
export { CryptoOnchainCheckout } from '../core/cryptoOnchainCheckout';
export type {
  CryptoOnchainCheckoutOptions,
  CryptoOnchainCheckoutState,
  CryptoOnchainSelection,
} from '../core/cryptoOnchainCheckout';
export { OrdersResource } from '../core/resources/orders';
export { OffersResource } from '../core/resources/offers';
export { ExpressPaymentMethodsResource } from '../core/resources/expressPaymentMethods';
export type { PaymentMethod as ExpressPaymentMethod } from '../core/resources/expressPaymentMethods';
export type { ShippingRate, ShippingRatesResponse, ShippingRatesPreviewParams } from '../core/resources/shippingRates';
export type {
    Payment,
    PaymentResponse,
    PaymentOptions,
    CardPaymentMethod,
    BasisTheoryInstance,
    GooglePayToken,
    ApplePayToken,
    PaymentInstrumentInput,
    PaymentInstrumentResponse,
    PaymentInstrumentCustomerResponse,
} from '../core/resources/payments';

// Re-export Store Config (for standalone resolvers / builders)
export { StoreConfigResource } from '../core/resources/storeConfig';
export type { StoreConfig } from '../core/resources/storeConfig';

// Re-export Geo Resource (for standalone geolocation / country detection)
export { GeoResource } from '../core/resources/geo';
export type { GeoLocationData } from '../core/resources/geo';

// Re-export 3DS Resource (for standalone resolvers / builders)
export { ThreedsResource } from '../core/resources/threeds';

// Re-export ISO Data Core (for standalone address form / resolvers)
export { ISODataCore } from '../core/isoData';
export type { ISOCountry, ISORegion } from '../core/isoData';

// Re-export Google Autocomplete Core (for standalone address form)
export { GoogleAutocompleteCore } from '../core/googleAutocomplete';
export type { GooglePrediction, GooglePlaceDetails, AddressComponents } from '../core/googleAutocomplete';

// Re-export Error Types
export {
    TagadaError,
    TagadaApiError,
    TagadaNetworkError,
    TagadaAuthError,
    TagadaValidationError,
    TagadaCircuitBreakerError,
    TagadaErrorCode,
    getServerErrorCode,
} from '../core/errors';
export type { TagadaErrorOptions, TagadaErrorCodeValue } from '../core/errors';

// Re-export Types
export type { TagadaClientConfig, TagadaState };
export { FunnelActionType } from '../core/resources/funnel';
export type { FunnelAction, FunnelNavigationResult, SimpleFunnelContext } from '../core/resources/funnel';

// Re-export Checkout Types (for external builders)
export type {
    CheckoutData,
    CheckoutSession,
    CheckoutSummary,
    CheckoutSummaryItem,
    CheckoutLineItem,
    CheckoutInitParams,
    Promotion,
    CheckoutCountryPreset,
    GetCheckoutOptions,
} from '../core/resources/checkout';

// Re-export Core Types (domain models)
export type {
    Order,
    OrderItem,
    OrderAddress,
    OrderSummary,
    OrderWithRelations,
    PaymentSummary,
    PromotionSummary,
    Subscription,
    DeviceInfo,
    PromotionCode,
    Customer,
    Store,
    PickupPoint,
} from '../core/types';

// Re-export Utilities (includes formatMoney from core/utils/currency)
export * from '../core/utils';

// Re-export path remapping utilities (for SPA routing)
export {
    matchRoute,
    getInternalPath,
    isPathRemapped,
    getPathInfo,
} from '../core/pathRemapping';

// Re-export Step Config / Payment Flow helpers (for standalone resolvers / builders)
export { getAssignedPaymentFlowId };
export { getAssignedStepConfig } from '../core/funnelClient';
export type { RuntimeStepConfig } from '../core/funnelClient';

// Re-export Payment Service (standalone payment processing — no React)
export { PaymentService, QR_CODE_EXPIRED_MESSAGE } from './payment-service';
export type {
    PaymentServiceConfig,
    PaymentResult,
    PaymentCallbacks,
    QrCodePaymentAction,
    CardData,
    ApmData,
} from './payment-service';

// Re-export Apple Pay Service (standalone native Apple Pay — no React)
export { isApplePayAvailable, startApplePaySession } from './apple-pay-service';
export type {
    ApplePayServiceConfig,
    ApplePayPaymentRequest,
    ApplePayTokenResult,
    ApplePayShippingContact,
    ApplePayCallbacks,
    ApplePayOutcome,
    ApplePaySessionCallbacks,
} from './apple-pay-service';

// Re-export Google Pay Service (standalone native Google Pay — no React)
export { isGooglePayAvailable, startGooglePaySession } from './google-pay-service';
export type {
    GooglePayServiceConfig,
    GooglePayPaymentRequest,
    GooglePayTokenResult,
    GooglePayAddress,
    GooglePayCallbacks,
} from './google-pay-service';

// Re-export currency helpers (no React dep) so standalone consumers (studio, vanilla
// checkouts, …) can do backend-minor → major → wallet-minor conversions without bringing
// in the React entry-point. NEVER hardcode `* 100` in callers — use these helpers, which
// read the per-currency ISO decimal count.
export {
    formatMoney,
    getCurrencyInfo,
    moneyStringOrNumberToMinorUnits,
    minorUnitsToMajorUnits,
} from '../../react/utils/money';

/**
 * Get BasisTheory public API key based on hostname detection.
 * Delegates to the centralized isProductionBasisTheory() logic.
 */
export function getBasisTheoryApiKey(): string {
    return _getBtApiKey();
}

/**
 * Get BasisTheory tenant ID based on hostname detection.
 */
export function getBasisTheoryTenantId(): string {
    return _getBtTenantId();
}

// ============================================================================
// EXTERNAL PAGE TRACKER
// ============================================================================
// Lightweight tracker for external pages not hosted on Tagadapay
// NOTE: This is also available as a standalone CDN bundle (external-tracker.min.js)
export {
    TagadaTracker,
    TagadaExternalTracker,
} from './external-tracker';

export type {
    TagadaTrackerConfig,
    ExternalTrackerSession,
    NavigateOptions,
} from './external-tracker';

// ============================================================================
// AD-TRACKER CLICK-ID RESOLVER
// ============================================================================
// Pure utility for resolving ClickFlare / Voluum / Binom / RedTrack / etc.
// click ids from URL params or cookies. Auto-published to
// `window.TagadaPay.tracking` on SDK load (see eager block above).
export {
    resolveClickId,
    publishTrackingGlobal,
    CLICK_ID_URL_PARAMS,
    CLICK_ID_COOKIES,
} from '../core/utils/clickIdResolver';

export type {
    ResolvedClickId,
    TagadaTrackingGlobal,
    ClickIdSource,
} from '../core/utils/clickIdResolver';
